import React, { useState, useEffect } from 'react';
import FormBuilder from '../components/FormBuilder';
import UserManagement from '../components/UserManagement';
import '../styles/Settings.css';

export default function Settings() {
  const [domain, setDomain] = useState('');
  const [sslStatus, setSslStatus] = useState('');
  const [syncInterval, setSyncInterval] = useState('3');
  const [feeds, setFeeds] = useState([]);
  const [outgoingUrl, setOutgoingUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingFeed, setEditingFeed] = useState(null);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedName, setNewFeedName] = useState('');
  const [newFeedType, setNewFeedType] = useState('airbnb');
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');

      const sslRes = await fetch('/api/settings/ssl-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sslRes.ok) {
        const data = await sslRes.json();
        setSslStatus(data.status);
      }

      const feedsRes = await fetch('/api/settings/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (feedsRes.ok) {
        const data = await feedsRes.json();
        setFeeds(data.feeds || []);
        setOutgoingUrl(data.outgoingUrl || '');
      }

      const intervalRes = await fetch('/api/settings/sync-interval', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (intervalRes.ok) {
        const data = await intervalRes.json();
        setSyncInterval(data.interval || '3');
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDomain = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/domain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ domain })
      });
      if (response.ok) {
        setSuccess('Domain saved. SSL certificate will be configured automatically.');
      } else {
        setError('Failed to save domain');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleUpdateFeed = async (feedId) => {
    if (!newFeedUrl) {
      setError('Feed URL cannot be empty');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/ical-feeds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feedId,
          sourceUrl: newFeedUrl,
          feedName: newFeedName,
          feedType: newFeedType
        })
      });
      if (response.ok) {
        setSuccess('Feed updated successfully!');
        setEditingFeed(null);
        setNewFeedUrl('');
        setNewFeedName('');
        fetchSettings();
      } else {
        setError('Failed to update feed');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('Delete this feed?')) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/settings/ical-feeds/${feedId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Feed deleted');
        fetchSettings();
      } else {
        setError('Failed to delete feed');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleSyncInterval = async (e) => {
    const interval = e.target.value;
    setSyncInterval(interval);
    setSuccess('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/sync-interval', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ interval: parseInt(interval) })
      });
      if (response.ok) {
        setSuccess(`Sync interval updated to every ${interval} hour(s) — applied immediately, no restart needed.`);
      } else {
        setError('Failed to update sync interval');
      }
    } catch (err) {
      setError('Failed to update sync interval');
    }
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1>Settings</h1>

        <div className="settings-tabs">
          <button
            className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`tab-btn ${activeTab === 'feeds' ? 'active' : ''}`}
            onClick={() => setActiveTab('feeds')}
          >
            iCal Feeds
          </button>
          <button
            className={`tab-btn ${activeTab === 'form' ? 'active' : ''}`}
            onClick={() => setActiveTab('form')}
          >
            Form Builder
          </button>
          <button
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {activeTab === 'general' && (
          <>
            <div className="settings-card">
              <h2>Domain & SSL Certificate</h2>
              <p>Configure your domain and Let's Encrypt SSL will be set up automatically.</p>
              <form onSubmit={handleSaveDomain}>
                <input
                  type="text"
                  placeholder="e.g., calendar.yourdomain.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                />
                <button type="submit">Save Domain</button>
              </form>
              {sslStatus && <p className="ssl-status">✓ {sslStatus}</p>}
            </div>
          </>
        )}

        {activeTab === 'feeds' && (
          <>
            <div className="settings-card">
              <h2>Sync Interval</h2>
              <p>How often should the system check Airbnb/Booking.com for new reservations?</p>
              <select value={syncInterval} onChange={handleSyncInterval}>
                <option value="1">Every 1 hour</option>
                <option value="3">Every 3 hours</option>
                <option value="6">Every 6 hours</option>
                <option value="12">Every 12 hours</option>
                <option value="24">Every 24 hours</option>
              </select>
            </div>

            <div className="settings-card">
              <h2>Outgoing iCal Feed</h2>
              <p>Add this URL to Airbnb and Booking.com so they see when you block dates:</p>
              <div className="feed-url-box">
                <input
                  type="text"
                  readOnly
                  value={outgoingUrl || ''}
                  onClick={(e) => e.target.select()}
                />
                <button onClick={() => {
                  navigator.clipboard.writeText(outgoingUrl || '');
                  setSuccess('URL copied to clipboard!');
                }}>Copy</button>
              </div>
            </div>

            <div className="settings-card">
              <h2>iCal Feeds</h2>
              <p>Manage your imported calendars from Airbnb and Booking.com:</p>

              {feeds.length === 0 ? (
                <p style={{ color: '#888' }}>No feeds configured yet</p>
              ) : (
                <div className="feeds-list">
                  {feeds.map((feed) => (
                    <div key={feed.id} className="feed-item">
                      <div className="feed-info">
                        <h3>{feed.feed_name}</h3>
                        <p className="feed-type">{feed.feed_type.toUpperCase()}</p>
                        <p className="feed-url">{feed.source_url}</p>
                        {feed.last_synced && (
                          <p className="feed-sync">Last synced: {new Date(feed.last_synced).toLocaleString()}</p>
                        )}
                      </div>
                      <div className="feed-actions">
                        <button
                          className="btn-edit"
                          onClick={() => {
                            setEditingFeed(feed.id);
                            setNewFeedName(feed.feed_name);
                            setNewFeedUrl(feed.source_url);
                            setNewFeedType(feed.feed_type);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteFeed(feed.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {editingFeed && (
                <div className="feed-edit-form">
                  <h3>Edit Feed</h3>
                  <input
                    type="text"
                    placeholder="Feed name"
                    value={newFeedName}
                    onChange={(e) => setNewFeedName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="iCal URL"
                    value={newFeedUrl}
                    onChange={(e) => setNewFeedUrl(e.target.value)}
                  />
                  <select value={newFeedType} onChange={(e) => setNewFeedType(e.target.value)}>
                    <option value="airbnb">Airbnb</option>
                    <option value="booking">Booking.com</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="form-actions">
                    <button onClick={() => handleUpdateFeed(editingFeed)} className="btn-save">Save</button>
                    <button onClick={() => setEditingFeed(null)} className="btn-cancel">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'form' && (
          <FormBuilder propertyId={1} />
        )}

        {activeTab === 'users' && (
          <UserManagement />
        )}
      </div>
    </div>
  );
}

